import { useState, useCallback, useEffect, useRef } from "react";
import Button from "@components/ui/buttons/Button";
import DropDown from "@components/ui/dropdown/DropDown";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import { useDataStore } from "@store/user_org";
import { isOwnerOrAdmin } from "@utils/permissions";
import { showNotification } from "context/notification.service";
import Icon1 from "@assets/icons/Icon";
import TextField from "@components/ui/textfields/TextField";
import { SwitchBrick } from "@components/ui/selection/SwitchBrick";
import { sessionsApi } from "@services/api/sessions";
import type { BookingAnalytics, BookingSeriesPoint } from "@services/api/sessions";
import { bookingsApi } from "@services/api/bookings";
import type { Booking } from "types/booking";

type MobilityOption = { key: string; label: string; sub: string };

/** Default "solution de mobilité" options. Keys (velo|pret|location) are fixed — the
 *  booking flow's fleet-availability logic depends on them — only label + description
 *  (sub) are garage-editable here. Must mirror DEFAULT_MOBILITY_OPTIONS in the booking
 *  widgets (InternalBookingPage.tsx + booking-iframe BookingFullPage.tsx). */
const DEFAULT_MOBILITY_OPTIONS: MobilityOption[] = [
  { key: "velo", label: "Vélo électrique", sub: "Gratuit" },
  { key: "pret", label: "Véhicule de prêt", sub: "Véhicule 15-20 ans, limité à 100 km" },
  { key: "location", label: "Véhicule de location", sub: "Véhicule récent, km illimités — 25€/jour" },
];

function resolveMobilityOptions(cfg?: MobilityOption[]): MobilityOption[] {
  return DEFAULT_MOBILITY_OPTIONS.map((d) => {
    const o = cfg?.find((c) => c.key === d.key);
    return o ? { key: d.key, label: o.label || d.label, sub: o.sub ?? d.sub } : { ...d };
  });
}

const MIN_DAYS_OPTIONS = [
  { value: "0", label: "Aujourd'hui (pas de marge)" },
  { value: "1", label: "1 jour" },
  { value: "2", label: "2 jours" },
  { value: "3", label: "3 jours" },
  { value: "5", label: "5 jours" },
  { value: "7", label: "1 semaine" },
  { value: "14", label: "2 semaines" },
  { value: "21", label: "3 semaines" },
  { value: "30", label: "1 mois" },
];

const SR_HOURS_OPTIONS = [
  { value: "0.5", label: "30 minutes" },
  { value: "1", label: "1 heure" },
  { value: "2", label: "2 heures" },
  { value: "3", label: "3 heures" },
  { value: "4", label: "4 heures" },
  { value: "6", label: "6 heures" },
  { value: "8", label: "8 heures" },
  { value: "12", label: "12 heures" },
  { value: "24", label: "1 jour" },
  { value: "48", label: "2 jours" },
  { value: "72", label: "3 jours" },
  { value: "96", label: "4 jours" },
  { value: "120", label: "5 jours" },
  { value: "144", label: "6 jours" },
  { value: "168", label: "1 semaine" },
  { value: "336", label: "2 semaines" },
];

const SAFETY_MARGIN_OPTIONS = [
  { value: "0", label: "Pas de marge" },
  { value: "0.5", label: "30 minutes" },
  { value: "1", label: "1 heure" },
  { value: "1.5", label: "1h30" },
  { value: "2", label: "2 heures" },
  { value: "3", label: "3 heures" },
  { value: "4", label: "4 heures" },
];

const SR_BUFFER_OPTIONS = [
  { value: "15", label: "15 minutes" },
  { value: "30", label: "30 minutes" },
  { value: "45", label: "45 minutes" },
  { value: "60", label: "1 heure" },
];

const SR_MAX_WAIT_OPTIONS = [
  { value: "30", label: "30 minutes" },
  { value: "45", label: "45 minutes" },
  { value: "60", label: "1 heure" },
  { value: "90", label: "1h30" },
  { value: "120", label: "2 heures" },
];

const MAX_DAYS_AHEAD_OPTIONS = [
  { value: "14", label: "2 semaines" },
  { value: "30", label: "1 mois" },
  { value: "60", label: "2 mois" },
  { value: "90", label: "3 mois" },
  { value: "120", label: "4 mois" },
  { value: "180", label: "6 mois" },
  { value: "365", label: "1 an" },
];

/** Generates time options every 15 min between two hours (inclusive), e.g. 7h00, 7h15, 7h30, 7h45. */
function quarterHourOptions(startHour: number, endHour: number): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];
  for (let h = startHour; h <= endHour + 1e-9; h += 0.25) {
    const hours = Math.floor(h);
    const minutes = Math.round((h - hours) * 60);
    options.push({
      value: String(h),
      label: `${hours}h${String(minutes).padStart(2, "0")}`,
    });
  }
  return options;
}

const OPENING_HOUR_OPTIONS = quarterHourOptions(7, 10);

const CLOSING_HOUR_OPTIONS = quarterHourOptions(16, 20);

const LUNCH_START_OPTIONS = quarterHourOptions(11, 13);

const LUNCH_END_OPTIONS = quarterHourOptions(12, 14.5);

const MAX_PER_DAY_OPTIONS = [
  { value: "0", label: "Illimité" },
  { value: "2", label: "2 interventions" },
  { value: "3", label: "3 interventions" },
  { value: "4", label: "4 interventions" },
  { value: "5", label: "5 interventions" },
  { value: "6", label: "6 interventions" },
  { value: "8", label: "8 interventions" },
  { value: "10", label: "10 interventions" },
  { value: "12", label: "12 interventions" },
  { value: "15", label: "15 interventions" },
];

type RangeId = "24h" | "7d" | "30d" | "90d";
const RANGES: { id: RangeId; label: string; days?: number; hours?: number }[] = [
  { id: "24h", label: "24h", hours: 24 },
  { id: "7d", label: "7 jours", days: 7 },
  { id: "30d", label: "30 jours", days: 30 },
  { id: "90d", label: "90 jours", days: 90 },
];

function fmtLeadDate(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function Legend({ color, label, dashed }: { color: string; label: string; dashed?: boolean }) {
  return (
    <div className="flex flex-row items-center gap-1.5">
      <div className="w-3" style={dashed ? { borderTop: `2px dashed ${color}` } : { height: 2, background: color, borderRadius: 2 }} />
      <span className="text-h4 text-neutral-400">{label}</span>
    </div>
  );
}

/** Evolution chart — visits (daily only), form opens, and confirmed bookings. */
function EvolutionChart({ series, hourly }: { series: BookingSeriesPoint[]; hourly: boolean }) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const opens = series.map((s) => s.opens);
  const completed = series.map((s) => s.completed);
  const visits = series.map((s) => s.visits);
  const maxV = Math.max(...visits, ...opens, 1);

  const chartW = 580, chartH = 170, leftMargin = 30, rightMargin = 12;
  const plotW = chartW - leftMargin - rightMargin;
  const toX = (i: number) => (series.length > 1 ? leftMargin + (i / (series.length - 1)) * plotW : leftMargin + plotW / 2);
  const toY = (v: number) => chartH - (v / maxV) * (chartH - 16);

  const line = (vals: number[]) => vals.map((v, i) => ({ x: toX(i), y: toY(v) }));
  const buildPath = (pts: { x: number; y: number }[]) =>
    pts.reduce((acc, p, i) => {
      if (i === 0) return `M ${p.x} ${p.y}`;
      const prev = pts[i - 1];
      const cpx = (prev.x + p.x) / 2;
      return acc + ` C ${cpx} ${prev.y}, ${cpx} ${p.y}, ${p.x} ${p.y}`;
    }, "");

  const openPts = line(opens), compPts = line(completed), visitPts = line(visits);
  const openPath = buildPath(openPts);
  const fillD = openPts.length ? `${openPath} L ${openPts[openPts.length - 1].x} ${chartH} L ${openPts[0].x} ${chartH} Z` : "";

  const bucketLabel = (b: string) =>
    hourly ? `${b.slice(11, 13)}h` : new Date(b).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }).replace(".", "");

  const labeled = series
    .map((s, i) => ({ i, b: s.bucket }))
    .filter((_, i) => {
      const n = series.length;
      return n <= 8 || i === 0 || i === n - 1 || i % Math.ceil(n / 6) === 0;
    });

  const handleMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg || !series.length) return;
    const rect = svg.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * chartW;
    let nearest = 0, min = Infinity;
    openPts.forEach((p, i) => { const d = Math.abs(p.x - x); if (d < min) { min = d; nearest = i; } });
    setHoveredIdx(nearest);
  }, [openPts]);

  const totalOpens = opens.reduce((a, b) => a + b, 0);

  return (
    <div className="flex flex-col gap-3 bg-white p-4 rounded-lg border-1">
      <div className="font-semibold text-black">Évolution</div>
      {totalOpens === 0 ? (
        <div className="flex items-center justify-center h-[170px] text-h4 text-neutral-300">Aucune activité sur la période</div>
      ) : (
        <svg ref={svgRef} viewBox={`0 0 ${chartW} ${chartH + 24}`} width="100%" className="overflow-visible" onMouseMove={handleMove} onMouseLeave={() => setHoveredIdx(null)} style={{ cursor: "crosshair" }}>
          <defs>
            <linearGradient id="evoFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#6366F1" stopOpacity="0.14" />
              <stop offset="100%" stopColor="#6366F1" stopOpacity="0.01" />
            </linearGradient>
          </defs>
          {[0, 0.25, 0.5, 0.75, 1].map((f) => { const y = toY(maxV * f); return <line key={f} x1={leftMargin} y1={y} x2={chartW - rightMargin} y2={y} stroke="#f0f0f0" />; })}
          {[0, 0.5, 1].map((f) => { const y = toY(maxV * f); return <text key={f} x={leftMargin - 6} y={y + 4} textAnchor="end" fontSize={10} fill="#9CA3AF">{Math.round(maxV * f)}</text>; })}
          {fillD && <path d={fillD} fill="url(#evoFill)" />}
          {!hourly && <path d={buildPath(visitPts)} fill="none" stroke="#D1D5DB" strokeWidth={2} />}
          <path d={openPath} fill="none" stroke="#6366F1" strokeWidth={2.5} />
          <path d={buildPath(compPts)} fill="none" stroke="#22C55E" strokeWidth={2} strokeDasharray="6 3" />
          {hoveredIdx !== null && openPts[hoveredIdx] && (
            <>
              <line x1={openPts[hoveredIdx].x} y1={0} x2={openPts[hoveredIdx].x} y2={chartH} stroke="#e5e7eb" strokeDasharray="4 2" />
              <circle cx={openPts[hoveredIdx].x} cy={openPts[hoveredIdx].y} r={4} fill="#6366F1" stroke="#fff" strokeWidth={2} />
              <g transform={`translate(${Math.min(Math.max(openPts[hoveredIdx].x - 60, leftMargin), chartW - rightMargin - 120)}, ${openPts[hoveredIdx].y < 60 ? openPts[hoveredIdx].y + 12 : openPts[hoveredIdx].y - 58})`}>
                <rect width={120} height={54} rx={8} fill="#1a1a1a" />
                <text x={60} y={15} textAnchor="middle" fontSize={10} fill="#c8c8c8">{bucketLabel(series[hoveredIdx].bucket)}</text>
                {!hourly && <text x={60} y={29} textAnchor="middle" fontSize={11} fill="#d1d5db">{series[hoveredIdx].visits} visites</text>}
                <text x={60} y={hourly ? 30 : 41} textAnchor="middle" fontSize={11} fill="#a5b4fc">{series[hoveredIdx].opens} ouvertures</text>
                <text x={60} y={hourly ? 44 : 51} textAnchor="middle" fontSize={11} fill="#86efac">{series[hoveredIdx].completed} RDV</text>
              </g>
            </>
          )}
          {labeled.map(({ i, b }) => <text key={i} x={toX(i)} y={chartH + 16} textAnchor="middle" fontSize={10} fill="#a3a3a3">{bucketLabel(b)}</text>)}
        </svg>
      )}
      <div className="flex flex-row items-center gap-4 flex-wrap">
        {!hourly && <Legend color="#D1D5DB" label="Visites" />}
        <Legend color="#6366F1" label="Ouvertures" />
        <Legend color="#22C55E" label="RDV" dashed />
      </div>
    </div>
  );
}

function KpiCard({ label, sub, value, color }: { label: string; sub: string; value: number | string; color: string }) {
  return (
    <div className="flex flex-col gap-0.5 bg-white p-4 rounded-lg border-1">
      <span className="text-h4 text-neutral-400">{label}</span>
      <span className="text-hx2 font-semibold leading-tight" style={{ color }}>{value}</span>
      <span className="text-h5 text-neutral-400">{sub}</span>
    </div>
  );
}

function FunnelBars({ rows, accentCompleted }: { rows: BookingAnalytics["funnel"]; accentCompleted?: boolean }) {
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <div className="flex flex-col gap-2">
      {rows.map((r) => (
        <div key={r.key} className="flex flex-row items-center gap-3">
          <div className="text-h4 text-neutral-500 w-140 shrink-0 truncate">{r.label}</div>
          <div className="flex-1 h-7 bg-neutral-100 rounded-md overflow-hidden">
            <div
              className="h-full rounded-md flex items-center px-2"
              style={{
                width: `${r.count > 0 ? Math.max((r.count / max) * 100, 8) : 0}%`,
                minWidth: r.count > 0 ? 44 : 0,
                background: accentCompleted && r.key === "completed" ? "#16A34A" : accentCompleted ? "#6366F1" : "#F97316",
              }}
            >
              <span className="text-h4 font-semibold text-white whitespace-nowrap">{r.count}</span>
            </div>
          </div>
          <div className="text-h4 text-neutral-400 w-40 text-right shrink-0">{r.pct}%</div>
        </div>
      ))}
    </div>
  );
}

/** "Statistiques" panel — visits, fill funnel, evolution, abandonment, leads. */
function AnalyticsPanel({ orgId }: { orgId: string }) {
  const [rangeId, setRangeId] = useState<RangeId>("30d");
  const [data, setData] = useState<BookingAnalytics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId) return;
    const r = RANGES.find((x) => x.id === rangeId)!;
    setLoading(true);
    sessionsApi
      .getAnalytics(orgId, { days: r.days, hours: r.hours })
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [orgId, rangeId]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row justify-between items-start gap-2 flex-wrap">
        <div className="flex flex-col">
          <div className="text-h2 font-semibold text-black">Statistiques</div>
          <div className="text-h4 text-neutral-400 mt-0.5">Suivez les visites, le remplissage et les rendez-vous manqués de votre widget.</div>
        </div>
        <div className="flex flex-row items-center gap-1 bg-neutral-100 rounded-lg p-0.5">
          {RANGES.map((r) => (
            <button
              key={r.id}
              onClick={() => setRangeId(r.id)}
              className="text-h4 font-medium px-2.5 py-1 rounded-md transition-colors"
              style={{
                background: rangeId === r.id ? "#fff" : "transparent",
                color: rangeId === r.id ? "#111827" : "#9CA3AF",
                boxShadow: rangeId === r.id ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
              }}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading || !data ? (
        <div className="flex items-center justify-center h-[120px] text-h4 text-neutral-300 bg-white rounded-lg border-1">
          {loading ? "Chargement…" : "Aucune donnée pour le moment"}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard label="Visites" sub="visiteurs uniques" value={data.visits} color="#1E1E1E" />
            <KpiCard label="Ouvertures" sub="formulaires démarrés" value={data.opens} color="#6366F1" />
            <KpiCard label="Rendez-vous" sub="réservations confirmées" value={data.completed} color="#16A34A" />
            <KpiCard label="Taux de conversion" sub={`${data.abandoned} abandon${data.abandoned > 1 ? "s" : ""}`} value={`${data.conversion_rate}%`} color={data.conversion_rate >= 30 ? "#16A34A" : "#EA580C"} />
          </div>

          <div className="flex flex-col gap-3 bg-white p-4 rounded-lg border-1">
            <div className="flex flex-col">
              <div className="font-semibold text-black">Entonnoir de remplissage</div>
              <div className="text-h4 text-neutral-400">Nombre de visiteurs ayant atteint chaque étape (et % des ouvertures).</div>
            </div>
            <FunnelBars rows={data.funnel} accentCompleted />
          </div>

          <EvolutionChart series={data.series} hourly={data.hourly} />

          <div className="flex flex-col gap-3 bg-white p-4 rounded-lg border-1">
            <div className="flex flex-col">
              <div className="font-semibold text-black">Formulaires abandonnés</div>
              <div className="text-h4 text-neutral-400">Étape à laquelle les visiteurs ont quitté sans réserver.</div>
            </div>
            {data.abandonment.length === 0 ? (
              <div className="text-h4 text-neutral-400 py-2">Aucun abandon sur la période 🎉</div>
            ) : (
              <FunnelBars rows={data.abandonment} />
            )}
          </div>

          <div className="flex flex-col gap-3 bg-white p-4 rounded-lg border-1">
            <div className="flex flex-col">
              <div className="font-semibold text-black">
                Prospects à relancer
                {data.leads.length > 0 && <span className="text-neutral-400 font-normal"> · {data.leads.length} contact{data.leads.length > 1 ? "s" : ""}</span>}
              </div>
              <div className="text-h4 text-neutral-400">Visiteurs ayant laissé leur email/téléphone mais n'ayant pas réservé.</div>
            </div>
            {data.leads.length === 0 ? (
              <div className="text-h4 text-neutral-400 py-2">Aucun prospect à relancer sur la période.</div>
            ) : (
              <div className="overflow-x-auto">
                <div className="min-w-[560px]">
                  <div className="grid grid-cols-[1.4fr_1.8fr_1.2fr_1.2fr_1fr] gap-2 pb-2 border-b border-neutral-200 text-h4 font-medium text-neutral-400">
                    <div>Prospect</div><div>Email</div><div>Téléphone</div><div>Dernière étape</div><div>Date</div>
                  </div>
                  {data.leads.map((l, i) => (
                    <div key={i} className="grid grid-cols-[1.4fr_1.8fr_1.2fr_1.2fr_1fr] gap-2 py-2.5 border-b border-neutral-100 text-h4 items-center">
                      <div className="font-medium text-black truncate">{l.name || "—"}</div>
                      <div className="truncate">{l.email ? <a href={`mailto:${l.email}`} className="text-blue-600 hover:underline">{l.email}</a> : <span className="text-neutral-300">—</span>}</div>
                      <div className="truncate">{l.phone ? <a href={`tel:${l.phone.replace(/[^\d+]/g, "")}`} className="text-blue-600 hover:underline">{l.phone}</a> : <span className="text-neutral-300">—</span>}</div>
                      <div className="text-neutral-500 truncate">{l.step}</div>
                      <div className="text-neutral-400 truncate">{fmtLeadDate(l.date)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const IFRAME_BASE = "https://booking.heric.hexaflow.fr";

function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="relative rounded-lg overflow-hidden border border-neutral-200">
      <pre
        className="text-h4 bg-neutral-950 text-neutral-200 p-4 overflow-x-auto whitespace-pre-wrap break-all"
        style={{ fontFamily: "monospace", lineHeight: 1.6 }}
      >
        {code}
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-h4 font-medium transition-colors"
        style={{ background: copied ? "#22c55e" : "#3f3f46", color: "#fff" }}
      >
        <Icon1 name={copied ? "done" : "copy"} size={12} color="#fff" />
        {copied ? "Copié" : "Copier"}
      </button>
    </div>
  );
}

/** Prominent full-width button that copies the complete, ready-to-send
 *  developer instructions (both embed options + explanations). */
function CopyDevInstructionsButton({ instructions }: { instructions: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(instructions).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };
  return (
    <button
      onClick={handleCopy}
      className="flex flex-row items-center justify-center gap-2.5 w-full rounded-xl px-5 py-4 font-semibold transition-colors"
      style={{ background: copied ? "#16A34A" : "#000", color: "#fff" }}
    >
      <Icon1 name={copied ? "done" : "copy"} size={16} color="#fff" />
      <span className="text-h3">
        {copied ? "Instructions copiées !" : "Copier toutes les instructions à envoyer au développeur"}
      </span>
    </button>
  );
}

export const PriseDeRDV = () => {
  const orgData = useDataStore((state) => state.orgData);
  const userData = useDataStore((state) => state.userData);
  const updateOrgData = useDataStore((state) => state.updateOrgData);

  const [minDaysAhead, setMinDaysAhead] = useState(
    String(orgData?.config?.booking_min_days_ahead ?? 1),
  );
  const [maxDaysAhead, setMaxDaysAhead] = useState(
    String(orgData?.config?.booking_max_days_ahead ?? 90),
  );
  const [maxPerDay, setMaxPerDay] = useState(
    String(orgData?.config?.booking_max_per_day ?? 0),
  );
  const [safetyMargin, setSafetyMargin] = useState(
    String(orgData?.config?.booking_safety_margin_hours ?? 1),
  );
  const [srMinHours, setSrMinHours] = useState(
    String(orgData?.config?.booking_sr_min_hours_ahead ?? 2),
  );
  const [srBuffer, setSrBuffer] = useState(
    String(orgData?.config?.sr_reception_buffer_minutes ?? 30),
  );
  const [srMaxWait, setSrMaxWait] = useState(
    String(orgData?.config?.sr_max_wait_minutes ?? 60),
  );
  const [capacitySR, setCapacitySR] = useState(
    String(orgData?.config?.booking_capacity_service_rapide ?? 0),
  );
  const [capacityMecanique, setCapacityMecanique] = useState(
    String(orgData?.config?.booking_capacity_mecanique ?? 0),
  );
  const [capacityDiagnostic, setCapacityDiagnostic] = useState(
    String(orgData?.config?.booking_capacity_diagnostic ?? 0),
  );
  const [capacityCarrosserie, setCapacityCarrosserie] = useState(
    String(orgData?.config?.booking_capacity_carrosserie ?? 0),
  );
  const [openingHour, setOpeningHour] = useState(
    String(orgData?.config?.booking_opening_hour ?? 8),
  );
  const [closingHour, setClosingHour] = useState(
    String(orgData?.config?.booking_closing_hour ?? 18),
  );
  const [lunchStart, setLunchStart] = useState(
    String(orgData?.config?.booking_lunch_start ?? 12),
  );
  const [lunchEnd, setLunchEnd] = useState(
    String(orgData?.config?.booking_lunch_end ?? 13),
  );
  const [googleReviewUrl, setGoogleReviewUrl] = useState(
    orgData?.config?.google_review_url || "",
  );
  const [askRestitutionDateLong, setAskRestitutionDateLong] = useState(
    orgData?.config?.booking_ask_restitution_date_long ?? false,
  );
  const [mobilityOptions, setMobilityOptions] = useState<MobilityOption[]>(() =>
    resolveMobilityOptions(orgData?.config?.booking_mobility_options),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const orgId = orgData?.id ?? "";

  // Preview state
  const [previewKey, setPreviewKey] = useState(0);
  const [previewMode, setPreviewMode] = useState<"mobile" | "web">("mobile");
  const previewVersion = "3";
  const [embedTab, setEmbedTab] = useState<"card" | "widget">("card");

  const handleSave = async () => {
    if (!orgData) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      await updateOrgData(orgData.id, {
        config: {
          booking_min_days_ahead: parseInt(minDaysAhead),
          booking_max_days_ahead: parseInt(maxDaysAhead),
          booking_max_per_day: parseInt(maxPerDay),
          booking_safety_margin_hours: parseFloat(safetyMargin),
          booking_sr_min_hours_ahead: parseFloat(srMinHours),
          sr_reception_buffer_minutes: parseInt(srBuffer),
          sr_max_wait_minutes: parseInt(srMaxWait),
          booking_capacity_service_rapide: parseInt(capacitySR),
          booking_capacity_mecanique: parseInt(capacityMecanique),
          booking_capacity_diagnostic: parseInt(capacityDiagnostic),
          booking_capacity_carrosserie: parseInt(capacityCarrosserie),
          booking_opening_hour: parseFloat(openingHour),
          booking_closing_hour: parseFloat(closingHour),
          booking_lunch_start: parseFloat(lunchStart),
          booking_lunch_end: parseFloat(lunchEnd),
          google_review_url: googleReviewUrl || undefined,
          booking_ask_restitution_date_long: askRestitutionDateLong,
          booking_mobility_options: mobilityOptions.map((o) => ({
            key: o.key,
            label: o.label.trim() || DEFAULT_MOBILITY_OPTIONS.find((d) => d.key === o.key)!.label,
            sub: o.sub.trim(),
          })),
        },
      });
      showNotification({ message: "Modifications enregistrées avec succès", type: "success" });
    } catch {
      const errorMessage = "Échec de l'enregistrement des modifications";
      setSaveError(errorMessage);
      showNotification({ message: errorMessage, type: "error" });
    }
    setIsSaving(false);
  };

  const resetPreview = useCallback(() => setPreviewKey((k) => k + 1), []);

  const cardCode = `<iframe
  id="heric-booking"
  src="${IFRAME_BASE}?org_id=${orgId}&mode=card&v=${previewVersion}"
  width="100%"
  height="320"
  style="border:none; border-radius:16px; box-shadow:0 4px 24px rgba(0,0,0,0.10);"
></iframe>
<script>
window.addEventListener('message', function(e) {
  if (e.data?.type === 'booking-widget-resize')
    document.getElementById('heric-booking').style.height = e.data.height + 'px';
});
</script>`;

  const widgetCode = `<iframe
  id="heric-widget"
  src="${IFRAME_BASE}?org_id=${orgId}&mode=widget&v=${previewVersion}"
  style="position:fixed; bottom:0; right:0; border:none; z-index:9999; width:200px; height:80px; transition:height 0.3s ease;"
></iframe>
<script>
window.addEventListener('message', function(e) {
  if (e.data.type === 'heric:state')
    document.getElementById('heric-widget').style.height = e.data.open ? '750px' : '80px';
});
</script>`;

  const garageName = orgData?.infos?.name || "notre garage";

  // Complete, self-explanatory message the garage forwards to whoever manages
  // their website — no prior context needed, both options + technical notes.
  const devInstructions = `Bonjour,

Nous utilisons Carvis pour la prise de rendez-vous en ligne de ${garageName}. Pourriez-vous intégrer notre module de réservation sur notre site web ?

Deux options sont possibles — vous pouvez installer l'une, l'autre, ou les deux. Il s'agit dans les deux cas d'une simple iframe : aucune bibliothèque ni dépendance à installer, rien à héberger de votre côté.

════════════════════════════════════════
OPTION 1 — Carte « Prendre rendez-vous »
════════════════════════════════════════
Une carte cliquable à insérer dans une section de page (par exemple une page « Prendre RDV », ou la page d'accueil). Au clic, le client est redirigé vers la page de réservation. La carte s'adapte automatiquement à la largeur de son conteneur.

Collez ce code à l'endroit voulu dans le HTML de la page :

${cardCode}

════════════════════════════════════════
OPTION 2 — Bouton flottant
════════════════════════════════════════
Un bouton « Prendre RDV » fixé en bas à droite de l'écran, visible sur toutes les pages du site. Idéal pour un accès permanent.

Collez ce code juste avant la balise </body> (il fonctionne sur l'ensemble du site) :

${widgetCode}

════════════════════════════════════════
Notes techniques
════════════════════════════════════════
• Les deux intégrations sont de simples <iframe> : rien à installer, rien à maintenir.
• La carte (Option 1) prend 100 % de la largeur de son conteneur et une hauteur fixe de 320 px.
• Le bouton flottant (Option 2) s'affiche par-dessus le contenu (z-index 9999) et ne gêne pas la navigation.
• La réservation s'ouvre dans un nouvel onglet — le client ne quitte pas votre site.
• Aucune donnée personnelle n'est collectée par le site : tout se passe sur la page de réservation sécurisée.

Merci beaucoup !`;

  return (
    <div className="flex flex-col h-fit pb-96 mt-10 mx-4 lg:ml-10 w-full max-w-700">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          <Text1>Prise de RDV</Text1>
          <Text2>Configuration du widget de réservation en ligne.</Text2>
          <Text3>
            Gérez les paramètres de réservation en ligne accessibles par vos clients
            depuis votre site web.
          </Text3>
        </div>

        {/* ── Statistiques (funnel analytics) ─────────────────── */}
        <AnalyticsPanel orgId={orgId} />

        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h3 font-semibold text-black">Horaires d'ouverture (réception)</div>
          <div className="text-h4 text-neutral-400">
            Plage horaire pendant laquelle les clients peuvent déposer ou récupérer leur véhicule.
            Aucun créneau de réception ou restitution ne sera proposé en dehors de ces horaires.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <DropDown
              label="Ouverture"
              value={openingHour}
              options={OPENING_HOUR_OPTIONS}
              onSelect={(v) => setOpeningHour(String(v))}
              disabled={!isOwnerOrAdmin(userData)}
            />
            <DropDown
              label="Fermeture"
              value={closingHour}
              options={CLOSING_HOUR_OPTIONS}
              onSelect={(v) => setClosingHour(String(v))}
              disabled={!isOwnerOrAdmin(userData)}
            />
          </div>

          <div className="text-h4 font-medium text-black mt-2">Pause déjeuner (réception)</div>
          <div className="text-h4 text-neutral-400">
            Plage horaire de pause déjeuner pendant laquelle aucun créneau de réception ou restitution ne sera proposé.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <DropDown
              label="Début"
              value={lunchStart}
              options={LUNCH_START_OPTIONS}
              onSelect={(v) => setLunchStart(String(v))}
              disabled={!isOwnerOrAdmin(userData)}
            />
            <DropDown
              label="Fin"
              value={lunchEnd}
              options={LUNCH_END_OPTIONS}
              onSelect={(v) => setLunchEnd(String(v))}
              disabled={!isOwnerOrAdmin(userData)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-4 bg-white p-4 rounded-lg border-1">
          <div className="text-h3 font-semibold text-black">Délais minimum de réservation</div>

          <div className="flex flex-col gap-2">
            <div className="text-h4 font-medium text-black">Interventions classiques</div>
            <div className="text-h4 text-neutral-400">
              Nombre de jours minimum entre aujourd'hui et le premier créneau disponible.
              Par exemple, "2 jours" signifie que les clients ne pourront pas réserver avant après-demain.
            </div>
            <DropDown
              label="Marge avant premier créneau"
              value={minDaysAhead}
              options={MIN_DAYS_OPTIONS}
              onSelect={(v) => setMinDaysAhead(String(v))}
              disabled={!isOwnerOrAdmin(userData)}
            />
          </div>

          <div className="border-t border-neutral-100" />

          <div className="flex flex-col gap-2">
            <div className="text-h4 font-medium text-black">Service Rapide</div>
            <div className="text-h4 text-neutral-400">
              Délai minimum en heures avant le premier créneau disponible pour les interventions
              marquées "Service Rapide". Ce délai remplace le délai classique ci-dessus pour les créneaux SR.
              Par exemple, "2 heures" signifie que les clients pourront réserver un créneau SR au plus tôt 2 heures après l'heure actuelle.
            </div>
            <DropDown
              label="Marge avant créneau SR"
              value={srMinHours}
              options={SR_HOURS_OPTIONS}
              onSelect={(v) => setSrMinHours(String(v))}
              disabled={!isOwnerOrAdmin(userData)}
            />
          </div>

          <div className="border-t border-neutral-100" />

          <div className="flex flex-col gap-2">
            <div className="text-h4 font-medium text-black">Temps de réception (buffer)</div>
            <div className="text-h4 text-neutral-400">
              Temps entre l'arrivée du client et le début de l'intervention par le mécanicien
              (accueil, paperasse, déplacement du véhicule).
            </div>
            <DropDown
              label="Buffer réception"
              value={srBuffer}
              options={SR_BUFFER_OPTIONS}
              onSelect={(v) => setSrBuffer(String(v))}
              disabled={!isOwnerOrAdmin(userData)}
            />
          </div>

          <div className="border-t border-neutral-100" />

          <div className="flex flex-col gap-2">
            <div className="text-h4 font-medium text-black">Attente maximum (Service Rapide)</div>
            <div className="text-h4 text-neutral-400">
              Temps maximum qu'un client peut attendre avant que le mécanicien ne commence l'intervention.
              Si aucun créneau libre ne permet de démarrer dans ce délai, le créneau est marqué indisponible.
            </div>
            <DropDown
              label="Attente max SR"
              value={srMaxWait}
              options={SR_MAX_WAIT_OPTIONS}
              onSelect={(v) => setSrMaxWait(String(v))}
              disabled={!isOwnerOrAdmin(userData)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h3 font-semibold text-black">Délai maximum de réservation</div>
          <div className="text-h4 text-neutral-400">
            Horizon maximum de réservation. Les clients ne pourront pas réserver au-delà de cette période.
          </div>
          <DropDown
            label="Horizon de réservation"
            value={maxDaysAhead}
            options={MAX_DAYS_AHEAD_OPTIONS}
            onSelect={(v) => setMaxDaysAhead(String(v))}
            disabled={!isOwnerOrAdmin(userData)}
          />
        </div>

        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h3 font-semibold text-black">Réservations en ligne — maximum par jour</div>
          <div className="text-h4 text-neutral-400">
            Au-delà de ce nombre de rendez-vous de <b>réception</b> sur une journée, le jour
            est marqué « complet » pour la réservation en ligne. Compte uniquement les RDV
            des colonnes <b>Réception</b> et <b>Carrosserie</b> (y compris ceux importés de
            ServiceBox) ; les colonnes Location, Véhicule de prêt et RDV Divers sont ignorées.
          </div>
          <DropDown
            label="Maximum par jour"
            value={maxPerDay}
            options={MAX_PER_DAY_OPTIONS}
            onSelect={(v) => setMaxPerDay(String(v))}
            disabled={!isOwnerOrAdmin(userData)}
          />
        </div>

        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h3 font-semibold text-black">Capacité journalière par spécialité</div>
          <div className="text-h4 text-neutral-400">
            Nombre maximum d'interventions planifiées par jour pour chaque spécialité.
            Au-delà de cette limite, la journée sera marquée comme complète pour cette spécialité.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <DropDown
              label="Service Rapide"
              value={capacitySR}
              options={MAX_PER_DAY_OPTIONS}
              onSelect={(v) => setCapacitySR(String(v))}
              disabled={!isOwnerOrAdmin(userData)}
            />
            <DropDown
              label="Mécanique"
              value={capacityMecanique}
              options={MAX_PER_DAY_OPTIONS}
              onSelect={(v) => setCapacityMecanique(String(v))}
              disabled={!isOwnerOrAdmin(userData)}
            />
            <DropDown
              label="Diagnostic"
              value={capacityDiagnostic}
              options={MAX_PER_DAY_OPTIONS}
              onSelect={(v) => setCapacityDiagnostic(String(v))}
              disabled={!isOwnerOrAdmin(userData)}
            />
            <DropDown
              label="Carrosserie"
              value={capacityCarrosserie}
              options={MAX_PER_DAY_OPTIONS}
              onSelect={(v) => setCapacityCarrosserie(String(v))}
              disabled={!isOwnerOrAdmin(userData)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h3 font-semibold text-black">Marge de sécurité (restitution)</div>
          <div className="text-h4 text-neutral-400">
            Temps supplémentaire ajouté à la durée estimée de l'intervention pour calculer
            l'heure de restitution la plus tôt possible. Par exemple, si l'intervention dure 1h30
            et la marge est de 1h, le client ne pourra pas choisir une restitution avant 2h30 après le dépôt.
          </div>
          <DropDown
            label="Marge après intervention"
            value={safetyMargin}
            options={SAFETY_MARGIN_OPTIONS}
            onSelect={(v) => setSafetyMargin(String(v))}
            disabled={!isOwnerOrAdmin(userData)}
          />
        </div>

        {/* ── Restitution logic + configurable option ───────────── */}
        <div className="flex flex-col gap-3 bg-white p-4 rounded-lg border-1">
          <div className="text-h3 font-semibold text-black">Date de restitution</div>
          <div className="text-h4 text-neutral-400">
            Lors de la prise de RDV, ce que le client voit à l'étape « Restitution »
            dépend de la <span className="font-medium text-neutral-600">durée estimée</span> de
            l'intervention (réglée dans Paramètres → Interventions) :
          </div>

          {/* In-UI explanation of the 3 cases */}
          <div className="flex flex-col gap-2">
            {[
              {
                tag: "Courte (< 2h30)",
                color: "#16A34A",
                bg: "#F0FDF4",
                border: "#BBF7D0",
                text: "Le client indique s'il attend sur place (véhicule prêt le jour même) ou choisit une heure de récupération dans la journée.",
              },
              {
                tag: "Longue (≥ 2h30) ou diagnostic",
                color: "#DC2626",
                bg: "#FEF2F2",
                border: "#FECACA",
                text: askRestitutionDateLong
                  ? "Option activée : le client choisit lui-même une date de restitution (minimum selon la durée estimée)."
                  : "Par défaut : le client voit « La date de restitution vous sera communiquée par votre garage » (il ne choisit pas de date).",
              },
              {
                tag: "Durée inconnue (0)",
                color: "#525252",
                bg: "#FAFAFA",
                border: "#E5E5E5",
                text: "Le client choisit une date de récupération (minimum 2 jours ouvrés après le dépôt).",
              },
            ].map((c) => (
              <div key={c.tag} className="flex flex-col gap-1 p-2.5 rounded-lg" style={{ background: c.bg, border: `1px solid ${c.border}` }}>
                <span className="text-h5 font-semibold" style={{ color: c.color }}>{c.tag}</span>
                <span className="text-h5" style={{ color: "#525252" }}>{c.text}</span>
              </div>
            ))}
          </div>

          <SwitchBrick
            checked={askRestitutionDateLong}
            onChange={(v) => setAskRestitutionDateLong(v)}
            title="Proposer une date de restitution pour les longues durées"
            description="Si activé, le client choisit une date de restitution même pour les interventions ≥ 2h30 (au lieu du message « communiquée par le garage »)."
          />
        </div>


        {/* ── Solutions de mobilité (labels + descriptions) ─────── */}
        <div className="flex flex-col gap-3 bg-white p-4 rounded-lg border-1">
          <div className="text-h3 font-semibold text-black">Solutions de mobilité</div>
          <div className="text-h4 text-neutral-400">
            Personnalisez le nom et la description des 3 solutions de mobilité proposées au client
            pendant l'immobilisation de son véhicule (étape « Solution de mobilité » de la prise de RDV).
            Laissez la description vide pour ne rien afficher sous le nom.
          </div>
          <div className="flex flex-col gap-3">
            {mobilityOptions.map((opt, idx) => (
              <div key={opt.key} className="flex flex-col gap-2 p-3 rounded-lg border border-neutral-200 bg-neutral-50">
                <TextField
                  label="Nom"
                  value={opt.label}
                  onChange={(v: string) =>
                    setMobilityOptions((prev) => prev.map((o, i) => (i === idx ? { ...o, label: v } : o)))
                  }
                  disabled={!isOwnerOrAdmin(userData)}
                />
                <TextField
                  label="Description"
                  value={opt.sub}
                  onChange={(v: string) =>
                    setMobilityOptions((prev) => prev.map((o, i) => (i === idx ? { ...o, sub: v } : o)))
                  }
                  disabled={!isOwnerOrAdmin(userData)}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h3 font-semibold text-black">Lien Google Avis</div>
          <div className="text-h4 text-neutral-400">
            Lien vers votre fiche Google Business pour recueillir les avis clients.
            Si renseigné, les clients ayant donné 5 étoiles verront une invitation à laisser un avis Google.
          </div>
          <TextField
            label="URL Google Avis"
            value={googleReviewUrl}
            onChange={(v: string) => setGoogleReviewUrl(v)}
            disabled={!isOwnerOrAdmin(userData)}
          />
        </div>

        <div className="flex flex-col gap-2">
          {saveError && (
            <div className="text-h4 text-red-500 mt-2">{saveError}</div>
          )}
          <Button
            className="mt-4"
            label={isSaving ? "Enregistrement..." : "Enregistrer les modifications"}
            onClick={handleSave}
            disabled={isSaving || !isOwnerOrAdmin(userData)}
          />
        </div>

        {/* ── Preview ─────────────────────────────────────────── */}
        <div className="flex flex-col gap-3 mt-4">
          <div className="flex flex-col">
            <div className="text-h2 font-semibold text-black">Prévisualisation</div>
            <div className="text-h4 text-neutral-400 mt-0.5">
              Aperçu de la page de réservation tel que vos clients la verront.
            </div>
          </div>

            {/* Mobile / Web toggle + Version toggle */}
          <div className="flex flex-row items-center gap-3">
            <div className="flex flex-row gap-1 p-1 bg-neutral-100 rounded-lg w-fit">
              {(["mobile", "web"] as const).map((mode) => (
                <button
                  key={mode}
                  onClick={() => { setPreviewMode(mode); setPreviewKey((k) => k + 1); }}
                  className="flex items-center gap-1.5 px-4 py-1.5 rounded-md text-h4 font-medium transition-colors"
                  style={{
                    background: previewMode === mode ? "#fff" : "transparent",
                    color: previewMode === mode ? "#1E1E1E" : "#A1A1A1",
                    boxShadow: previewMode === mode ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                  }}
                >
                  <Icon1 name={mode === "mobile" ? "smartphone" : "desktop"} size={12} color={previewMode === mode ? "#1E1E1E" : "#A1A1A1"} />
                  {mode === "mobile" ? "Mobile" : "Web"}
                </button>
              ))}
            </div>
          </div>

          {/* Mock browser frame */}
          <div className="flex flex-col rounded-xl overflow-hidden border border-neutral-200 shadow-sm">
            {/* Browser chrome */}
            <div className="flex flex-row items-center gap-2 px-4 bg-neutral-100 border-b border-neutral-200" style={{ height: 40 }}>
              <div className="flex flex-row gap-1.5">
                <div className="rounded-full bg-neutral-300" style={{ width: 10, height: 10 }} />
                <div className="rounded-full bg-neutral-300" style={{ width: 10, height: 10 }} />
                <div className="rounded-full bg-neutral-300" style={{ width: 10, height: 10 }} />
              </div>
              <div className="flex-1 flex items-center justify-center">
                <div className="flex flex-row items-center gap-1.5 px-3 bg-white rounded-md border border-neutral-200" style={{ height: 24 }}>
                  <Icon1 name="lock" size={9} color="#A1A1A1" />
                  <span className="text-h4 text-neutral-400" style={{ fontSize: 10 }}>
                    votresite.fr
                  </span>
                </div>
              </div>
              <button
                onClick={resetPreview}
                className="flex items-center justify-center rounded-md hover:bg-neutral-200 transition-colors"
                style={{ width: 26, height: 26 }}
                title="Réinitialiser la prévisualisation"
              >
                <Icon1 name="restart" size={12} color="#A1A1A1" />
              </button>
            </div>

            {/* Widget preview area */}
            <div className="bg-neutral-50 flex justify-center" style={{ padding: previewMode === "mobile" ? "32px 16px" : "32px 24px" }}>
              <iframe
                key={previewKey}
                src={`${IFRAME_BASE}?org_id=${orgId}&mode=${previewMode === "mobile" ? "page" : "card"}&v=${previewVersion}`}
                style={{
                  width: previewMode === "mobile" ? 375 : "100%",
                  height: 700,
                  border: previewMode === "mobile" ? "1px solid #E5E5E5" : "none",
                  borderRadius: 16,
                  display: "block",
                }}
                title="Prévisualisation"
              />
            </div>
          </div>
        </div>

        {/* ── Embed code ──────────────────────────────────────── */}
        <div className="flex flex-col gap-3 mt-2">
          <div className="flex flex-col">
            <div className="text-h2 font-semibold text-black">Intégrer sur votre site</div>
            <div className="text-h4 text-neutral-400 mt-0.5">
              Copiez le code et collez-le dans le HTML de votre site web.
            </div>
          </div>

          {/* Mode tabs */}
          <div className="flex flex-row gap-1 p-1 bg-neutral-100 rounded-lg w-fit">
            {(["card", "widget"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setEmbedTab(tab)}
                className="px-4 py-1.5 rounded-md text-h4 font-medium transition-colors"
                style={{
                  background: embedTab === tab ? "#fff" : "transparent",
                  color: embedTab === tab ? "#1E1E1E" : "#A1A1A1",
                  boxShadow: embedTab === tab ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                }}
              >
                {tab === "card" ? "Carte centrée" : "Widget flottant"}
              </button>
            ))}
          </div>

          {/* Description */}
          <div className="text-h4 text-neutral-500">
            {embedTab === "card"
              ? "À intégrer dans une section de votre page (ex. page 'Prendre RDV'). Vous contrôlez la position."
              : "À coller avant </body>. Un bouton flottant apparaît en bas à droite de toutes vos pages."}
          </div>

          <CodeBlock code={embedTab === "card" ? cardCode : widgetCode} />

          {/* One-click: copy a complete, self-explanatory message for the site's developer */}
          <div className="flex flex-col gap-2 mt-3 pt-4 border-t border-neutral-200">
            <div className="text-h4 text-neutral-500">
              Vous ne gérez pas votre site vous-même ? Envoyez tout à votre développeur en un clic :
              le message inclut les deux options d'intégration, le code prêt à coller et les explications.
            </div>
            <CopyDevInstructionsButton instructions={devInstructions} />
          </div>
        </div>
      </div>
    </div>
  );
};
